import {type CSSDimension} from 'platform/foundation';
import styled from 'styled-components';

import {mergeRefs} from 'shared/web';

import {DashboardStretchRegion} from '../../../constants/DashboardStretchRegion';
import {DashboardAdaptiveDensityController} from '../../../types/DashboardAdaptiveDensityController';
import {type VehicleWarehouseMotLayout} from '../../../utils/getVehicleWarehouseMotLayout';
import {StyledVisibleMotContent} from '../constants/StyledVisibleMotContent';
import {useVehicleWarehouseMotLayout} from '../hooks/useVehicleWarehouseMotLayout';
import {VehicleWarehouseAdaptiveMotProps} from '../types/VehicleWarehouseAdaptiveMotProps';
import {renderVehicleWarehouseMotContent} from '../utils/renderVehicleWarehouseMotContent';
import {VehicleWarehouseMotProbes} from './VehicleWarehouseMotProbes';

interface VehicleWarehouseMeasuredMotProps extends VehicleWarehouseAdaptiveMotProps {
  baselineLayout: VehicleWarehouseMotLayout;
  coordinator: DashboardAdaptiveDensityController;
}

interface VehicleWarehouseMotRegionProps {
  $minimumHeight: CSSDimension;
  $isMeasuring: boolean;
}

export function VehicleWarehouseMeasuredMot(props: VehicleWarehouseMeasuredMotProps) {
  const motLayout = useVehicleWarehouseMotLayout(props);

  return (
    <VehicleWarehouseMotRegion
      ref={mergeRefs(motLayout.regionMeasurementRef, motLayout.regionElementRef)}
      $minimumHeight={motLayout.minimumHeight}
      $isMeasuring={!motLayout.hasInitialMeasurements}
      data-vehicle-warehouse-mot-region
      data-vehicle-warehouse-mot-layout={
        motLayout.hasInitialMeasurements ? 'measured' : 'measuring'
      }
      onFocusCapture={motLayout.onFocusCapture}
      onBlurCapture={motLayout.onBlurCapture}
    >
      <StyledVisibleMotContent data-vehicle-warehouse-mot-mode={motLayout.layout.mode}>
        {renderVehicleWarehouseMotContent(props, motLayout.layout)}
      </StyledVisibleMotContent>
      <VehicleWarehouseMotProbes
        {...props}
        rowLayoutKey={motLayout.rowLayoutKey}
        setters={motLayout.setters}
      />
    </VehicleWarehouseMotRegion>
  );
}

const VehicleWarehouseMotRegion = styled(DashboardStretchRegion)<VehicleWarehouseMotRegionProps>`
  --dashboard-adaptive-min-height: ${({$minimumHeight}) => $minimumHeight};

  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: ${({$isMeasuring}) =>
    $isMeasuring ? 'auto' : 'var(--dashboard-adaptive-min-height)'};
  overflow: ${({$isMeasuring}) => ($isMeasuring ? 'visible' : 'hidden')};
  contain: ${({$isMeasuring}) => ($isMeasuring ? 'none' : 'size')};
`;
